import { Reference } from '@volkswagen-onehub/disclaimer-manager';
import { LayerHandleV2, LayerManagerV25 } from '@volkswagen-onehub/layer-manager';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useFeatureServices, useIsType6DisclaimersEnabled } from 'src/feature-app/hooks';
import { DisclaimerLayerContent } from 'src/components';
import { CTA } from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';

export interface NamedDisclaimer {
  readonly ref: string;
  readonly text: string;
}

export type DisclaimerType = string | NamedDisclaimer;
export type DisclaimerReference = string | number;

export interface RegisteredDisclaimer {
  readonly text: string;
  readonly ref: Reference | undefined;
  readonly onClick?: () => void;
}

export interface RegisteredDisclaimers {
  [key: string]: RegisteredDisclaimer;
}

export function getDisclaimerText(disclaimer: DisclaimerType): string | undefined {
  return typeof disclaimer === 'string' ? disclaimer : disclaimer?.text;
}

export function isStandardReferenceDisclaimer(disclaimer: DisclaimerType): disclaimer is string {
  return typeof disclaimer === 'string';
}

export function calculateAddedDisclaimers(
  newDisclaimers: DisclaimerType[],
  registeredDisclaimersMap: RegisteredDisclaimers | undefined
): DisclaimerType[] {
  if (!registeredDisclaimersMap) {
    return newDisclaimers;
  }

  return newDisclaimers.filter((newDisclaimer) => !registeredDisclaimersMap[getDisclaimerText(newDisclaimer)]);
}

function isIncludedInNewDisclaimer(currentDisclaimerText: string, newDisclaimers: DisclaimerType[]): boolean {
  return newDisclaimers.map((newDisclaimer) => getDisclaimerText(newDisclaimer)).includes(currentDisclaimerText);
}

export function calculateRemovedDisclaimers(
  newDisclaimers: DisclaimerType[],
  registeredDisclaimersMap: RegisteredDisclaimers | undefined
): DisclaimerType[] {
  if (!registeredDisclaimersMap) {
    return [];
  }

  return Object.entries<RegisteredDisclaimer>(registeredDisclaimersMap)
    .map(([registeredDisclaimerText, registeredDisclaimer]) => {
      if (registeredDisclaimer && !isIncludedInNewDisclaimer(registeredDisclaimerText, newDisclaimers)) {
        return registeredDisclaimer.text;
      }

      return undefined;
    })
    .filter(Boolean);
}

export function useDisclaimerReference(disclaimers: DisclaimerType[] | undefined) {
  const disclaimerManager = useFeatureServices()['disclaimer-manager'].getScopedDisclaimerManager();
  const layerManager = useFeatureServices()['layer-manager'] as LayerManagerV25;
  const dispatch = useDispatch();
  const [registeredDisclaimers, setRegisteredDisclaimers] = useState<RegisteredDisclaimers | undefined>();
  const type6DisclaimersEnabled = useIsType6DisclaimersEnabled();
  const footerOrModuleDisclaimer =
    type6DisclaimersEnabled && 'openDisclaimerLayer' in layerManager ? 'module-reference' : 'footer-reference';

  const renderType6DisclaimerLayer = (
    disclaimerText: string,
    reference: string,
    closeHandler: () => void
  ): JSX.Element => {
    return (
      <DisclaimerLayerContent
        closeButton={
          <CTA
            tag="button"
            emphasis="tertiary"
            icon={<Close ariaHidden />}
            onClick={() => closeHandler()}
          />
        }
        text={disclaimerText}
        reference={reference}
      />
    );
  };

  const registerDisclaimer = (disclaimer: DisclaimerType, registeredDisclaimersMap: RegisteredDisclaimers) => {
    const text = getDisclaimerText(disclaimer);

    if (!text) {
      return;
    }

    if (isStandardReferenceDisclaimer(disclaimer)) {
      const reference = disclaimerManager.registerReferenceDisclaimer(footerOrModuleDisclaimer, text);

      let layerHandle: LayerHandleV2<undefined> | undefined;

      const onClick =
        type6DisclaimersEnabled && 'openDisclaimerLayer' in layerManager
          ? () => {
            if (layerHandle && !layerHandle.isClosed()) {
              layerHandle.close();
              layerHandle = undefined;
            } else {
              layerHandle = layerManager.openDisclaimerLayer(
                (_state, close) => renderType6DisclaimerLayer(disclaimer, reference, close),
                undefined,
                { userCloseable: true }
              );
            }
          }
          : undefined;

      registeredDisclaimersMap[text] = {
        text: disclaimer,
        ref: reference,
        onClick,
      };
    } else {
      if (disclaimer.ref) {
        // i.e. named disclaimer
        const reference = disclaimerManager.registerReferenceDisclaimer(footerOrModuleDisclaimer, text, disclaimer.ref);

        let layerHandle: LayerHandleV2<undefined> | undefined;

        const onClick =
          type6DisclaimersEnabled && 'openDisclaimerLayer' in layerManager
            ? () => {
              if (layerHandle && !layerHandle.isClosed()) {
                layerHandle.close();
                layerHandle = undefined;
              } else {
                layerHandle = layerManager.openDisclaimerLayer(
                  (_state, close) => renderType6DisclaimerLayer(disclaimer.text, disclaimer.ref, close),
                  undefined,
                  { userCloseable: true }
                );
              }
            }
            : undefined;

        registeredDisclaimersMap[text] = {
          text: disclaimer.text,
          ref: reference,
          onClick,
        };
      } else {
        disclaimerManager.registerGlobalDisclaimer(text);

        registeredDisclaimersMap[text] = {
          text: disclaimer.text,
          ref: undefined,
        };
      }
    }
  };

  const unregisterDisclaimer = (disclaimer: DisclaimerType, registeredDisclaimersMap: RegisteredDisclaimers) => {
    const text = getDisclaimerText(disclaimer);

    if (!text) {
      return;
    }

    registeredDisclaimersMap[text] = undefined;

    if (isStandardReferenceDisclaimer(disclaimer)) {
      disclaimerManager.unregisterReferenceDisclaimer(footerOrModuleDisclaimer, text);
    } else {
      if (disclaimer.ref) {
        // i.e. named disclaimer
        disclaimerManager.unregisterReferenceDisclaimer(footerOrModuleDisclaimer, text, disclaimer.ref);
      } else {
        disclaimerManager.unregisterGlobalDisclaimer(text);
      }
    }
  };

  /**
   * Unregister all disclaimers not
   * contained in update, register new disclaimers
   *
   * @param registeredDisclaimersMap
   *
   */
  const handlePropsChange = (
    guardedDisclaimers: DisclaimerType[],
    registeredDisclaimersMap: RegisteredDisclaimers
  ): void => {
    const removedDisclaimers = calculateRemovedDisclaimers(guardedDisclaimers, registeredDisclaimers);

    removedDisclaimers.forEach((disclaimer) => unregisterDisclaimer(disclaimer, registeredDisclaimersMap));

    const addedDisclaimers = calculateAddedDisclaimers(guardedDisclaimers, registeredDisclaimers);

    addedDisclaimers.forEach((disclaimer) => registerDisclaimer(disclaimer, registeredDisclaimersMap));

    if (addedDisclaimers.length + removedDisclaimers.length > 0) {
      setRegisteredDisclaimers(registeredDisclaimersMap);
    }
  };

  useEffect(() => {
    const guardedDisclaimers = disclaimers ?? [];
    const registeredDisclaimersMap: RegisteredDisclaimers = {};

    const isCalledTheFirstTime = !registeredDisclaimers;
    if (isCalledTheFirstTime) {
      guardedDisclaimers.forEach((disclaimer) => registerDisclaimer(disclaimer, registeredDisclaimersMap));

      setRegisteredDisclaimers(registeredDisclaimersMap);
    } else {
      handlePropsChange(guardedDisclaimers, registeredDisclaimersMap);
    }

    dispatch({ type: 'UPDATE_DISCLAIMERS', payload: registeredDisclaimers });

    // return () => {
    //   disclaimers?.map((disclaimer) =>
    //     unregisterDisclaimer(disclaimer, registeredDisclaimersMap)
    //   );
    // };
  }, [disclaimers]);

  return (
    registeredDisclaimers &&
    Object.entries<RegisteredDisclaimer>(registeredDisclaimers)
      .map(([_key, disclaimer]) => disclaimer)
      .filter(Boolean)
  );
}
